import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiTableMaintenanceWindow,
  ApiTableMaintenanceWindowCreate,
  ApiTableMaintenanceWindowUpdate,
  PaginatedResponse,
} from "@/types/api";

// CRUD under ``/api/table-maintenance/`` — a table out of service for a date
// range. Location-scoped via ``X-Location-Id``.
const MAINTENANCE_FIELDS = [
  "id",
  "table_id",
  "date",
  "end_date",
  "reason",
] as const satisfies readonly (keyof ApiTableMaintenanceWindow)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useTableMaintenance() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["table-maintenance", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: MAINTENANCE_FIELDS.join(",") });
      const res = await api
        .get(`table-maintenance/?${params}`)
        .json<PaginatedResponse<ApiTableMaintenanceWindow>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateTableMaintenance() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["table-maintenance", organizationId, locationId];

  return useMutation({
    mutationFn: (data: ApiTableMaintenanceWindowCreate) =>
      api.post("table-maintenance/", { json: data }).json<ApiTableMaintenanceWindow>(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateTableMaintenance() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["table-maintenance", organizationId, locationId];

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiTableMaintenanceWindowUpdate }) =>
      api.patch(`table-maintenance/${id}/`, { json: changes }).json<ApiTableMaintenanceWindow>(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteTableMaintenance() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["table-maintenance", organizationId, locationId];

  return useMutation({
    mutationFn: (id: string) => api.delete(`table-maintenance/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
